import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO component — sets <title>, meta description, canonical,
 * Open Graph / Twitter card overrides, robots, and optional JSON-LD.
 *
 * Defaults are tuned for ClientFlow (Norwegian booking + CRM SaaS).
 * Usage:
 *   <SEO
 *     title="..."
 *     description="..."
 *     path="/logg-inn"
 *     noindex
 *     jsonLd={{...}}
 *   />
 *
 * `path` is the canonical pathname (e.g. "/", "/vilkar"). The component
 * always builds the canonical against the production domain so previews
 * and dev origins never leak into the index.
 */
const SITE = "https://clientflow.no";
const DEFAULT_OG = `${SITE}/og-image.png`;
const DEFAULT_TWITTER = `${SITE}/twitter-card.png`;

export default function SEO({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG,
  twitterImage = DEFAULT_TWITTER,
  noindex = false,
  jsonLd = null,
}) {
  const canonical = `${SITE}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
  const fullTitle = title || "ClientFlow — Bookingsystem & CRM for norske småbedrifter";
  const desc =
    description ||
    "Smart bookingsystem og CRM for salonger, behandlere og servicebedrifter i Norge. Online timebestilling, kundekartotek og automatiske påminnelser.";

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="ClientFlow — bookingsystem og CRM for norske småbedrifter." />
      <meta property="og:locale" content="nb_NO" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="ClientFlow" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:image:alt" content="ClientFlow — bookingsystem og CRM for norske småbedrifter." />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
